import { useState, useEffect, useCallback } from 'react'
import { useSearchParams, Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Search as SearchIcon,
  Loader2,
  AlertCircle,
  FileText,
  Megaphone,
  Trophy,
  BookOpen,
  Quote,
  MapPin,
  ChevronRight,
  X,
  SlidersHorizontal,
  Sparkles,
} from 'lucide-react'
import { PortalLayout } from '../../components/portal/PortalLayout'
import { Seo } from '../../components/portal/Seo'
import { SearchAutocomplete } from '../../components/portal/SearchAutocomplete'
import { Container, Section, SectionHeading, Breadcrumbs, Card, Badge, EmptyState } from '../../components/portal/ui'
import {
  listSearch,
  suggestSearch,
  SEARCH_TYPE_LABELS,
  type SearchResult,
  type SearchType,
  type Suggestion,
} from '../../services/searchService'

// Per-type icon + tone used on result cards and the filter chips.
const TYPE_META: Record<SearchType, { icon: typeof FileText; tone: string }> = {
  ARTICLE: { icon: FileText, tone: 'blue' },
  CAMPAIGN: { icon: Megaphone, tone: 'green' },
  COMPETITION: { icon: Trophy, tone: 'amber' },
  RESOURCE: { icon: BookOpen, tone: 'teal' },
  TESTIMONIAL: { icon: Quote, tone: 'slate' },
  FACILITY: { icon: MapPin, tone: 'blue' },
}

const ALL_TYPES = Object.keys(SEARCH_TYPE_LABELS) as SearchType[]
const SIZE = 12

export default function SearchResultsPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const initialQ = params.get('q') || ''
  const initialType = (params.get('type') as SearchType | null) || null

  const [q, setQ] = useState(initialQ)
  const [type, setType] = useState<SearchType | null>(initialType)

  const [items, setItems] = useState<SearchResult[]>([])
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Curated popular topics for the quick-search chips (multilingual via Accept-Language).
  const [popularTopics, setPopularTopics] = useState<Suggestion[]>([])
  useEffect(() => {
    let cancelled = false
    suggestSearch('')
      .then((res) => {
        if (!cancelled) setPopularTopics(res.filter((s) => s.curated))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [i18n.language])

  // Debounce the free-text query so each keystroke doesn't fire a request.
  const [qDebounced, setQDebounced] = useState(initialQ)
  useEffect(() => {
    const td = setTimeout(() => setQDebounced(q), 300)
    return () => clearTimeout(td)
  }, [q])

  // Reflect query + type filter in the URL (shareable links, back-button works).
  useEffect(() => {
    const next = new URLSearchParams(params)
    if (qDebounced) next.set('q', qDebounced)
    else next.delete('q')
    if (type) next.set('type', type)
    else next.delete('type')
    setParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDebounced, type])

  const typeLabel = (key: SearchType) =>
    t(`pages.search.types.${key}`, { defaultValue: SEARCH_TYPE_LABELS[key] })

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await listSearch({
        q: qDebounced || undefined,
        type: type ?? undefined,
        page,
        size: SIZE,
      })
      setItems(data.content)
      setTotal(data.totalElements)
      setTotalPages(data.totalPages)
    } catch (e: any) {
      setError(e?.response?.data?.message || t('pages.search.error'))
    } finally {
      setLoading(false)
    }
  }, [qDebounced, type, page, t])

  useEffect(() => { load() }, [load])
  // Reset to first page whenever the query or type filter changes.
  useEffect(() => { setPage(0) }, [qDebounced, type])

  const clearAll = () => {
    setQ('')
    setType(null)
  }

  const activeFilterCount = (qDebounced ? 1 : 0) + (type ? 1 : 0)

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setQDebounced(q) // flush debounce immediately on submit
  }

  // Picking a curated topic runs that search on this page; a live content hit
  // navigates straight to the item.
  const pickSuggestion = (s: Suggestion) => {
    if (s.curated) {
      setQ(s.query)
      setQDebounced(s.query)
    } else {
      navigate(s.path)
    }
  }

  // Submitting raw typed text (no highlighted suggestion) runs the search here.
  const submitQuery = (query: string) => {
    setQ(query)
    setQDebounced(query)
  }

  const runTopic = (s: Suggestion) => {
    setQ(s.query)
    setQDebounced(s.query)
  }

  return (
    <PortalLayout>
      <Seo
        title={qDebounced ? t('pages.search.seo.titleQuery', { q: qDebounced }) : t('pages.search.seo.title')}
        description={t('pages.search.seo.description')}
        canonicalPath="/search"
        breadcrumbs={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.search', 'Search'), path: '/search' }]}
        noIndex
      />
      <Section className="bg-gradient-mesh pb-6">
        <Container>
          <Breadcrumbs items={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.search', 'Search') }]} />
          <SectionHeading
            as="h1"
            eyebrow={t('pages.search.hero.eyebrow')}
            title={t('pages.search.hero.title')}
            description={t('pages.search.hero.description')}
          />

          {/* Search input with autocomplete */}
          <form onSubmit={submit} role="search" className="relative max-w-2xl">
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-snow-400 pointer-events-none z-10" aria-hidden="true" />
            <SearchAutocomplete
              value={q}
              onChange={setQ}
              onSelectSuggestion={pickSuggestion}
              onSubmit={submitQuery}
              inputId="portal-search"
              srLabel={t('pages.search.searchSrLabel')}
              placeholder={t('pages.search.placeholder')}
              inputClassName="input pl-11 pr-10 text-base"
              leadingIcon={null}
            />
            {q && (
              <button
                type="button"
                onClick={() => {
                  setQ('')
                  setQDebounced('')
                }}
                aria-label={t('pages.search.clearSearch')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 rounded-lg hover:bg-snow-100 text-snow-500"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <button type="submit" className="sr-only">{t('pages.search.submit')}</button>
          </form>

          {/* Popular topics — quick-search chips for the portal's core themes */}
          {popularTopics.length > 0 && (
            <div className="max-w-3xl mt-4">
              <p className="inline-flex items-center gap-1.5 text-xs font-medium text-snow-500 mb-2">
                <Sparkles className="w-3.5 h-3.5 text-[rgb(0_180_216)]" aria-hidden="true" />
                {t('pages.search.popularTopics', 'Popular topics')}
              </p>
              <div className="flex flex-wrap gap-2">
                {popularTopics.map((s) => {
                  const on = qDebounced.toLowerCase() === s.query.toLowerCase()
                  return (
                    <button
                      key={s.query}
                      type="button"
                      onClick={() => runTopic(s)}
                      aria-pressed={on}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition border ${
                        on
                          ? 'bg-[rgb(15_76_129)] text-white border-[rgb(15_76_129)]'
                          : 'bg-white text-snow-700 border-snow-200 hover:border-[rgb(15_76_129)]/40 hover:text-[rgb(15_76_129)]'
                      }`}
                    >
                      {s.label}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </Container>
      </Section>

      <Section className="bg-white pt-4">
        <Container>
          {/* Type filter chips */}
          <div className="flex items-center gap-2 flex-wrap mb-6">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-snow-500 mr-1">
              <SlidersHorizontal className="w-3.5 h-3.5" aria-hidden="true" /> {t('pages.search.filter')}
            </span>
            <button
              onClick={() => setType(null)}
              aria-pressed={type === null}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition border ${
                type === null ? 'bg-[rgb(15_76_129)] text-white border-[rgb(15_76_129)]' : 'bg-white text-snow-700 border-snow-200 hover:bg-snow-50'
              }`}
            >
              {t('pages.search.all')}
            </button>
            {ALL_TYPES.map((tp) => {
              const Icon = TYPE_META[tp].icon
              const on = type === tp
              return (
                <button
                  key={tp}
                  onClick={() => setType(on ? null : tp)}
                  aria-pressed={on}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition border ${
                    on ? 'bg-[rgb(15_76_129)] text-white border-[rgb(15_76_129)]' : 'bg-white text-snow-700 border-snow-200 hover:bg-snow-50'
                  }`}
                >
                  <Icon className="w-4 h-4" aria-hidden="true" />
                  {typeLabel(tp)}
                </button>
              )
            })}
            {activeFilterCount > 0 && (
              <button onClick={clearAll} className="text-xs text-[rgb(15_76_129)] hover:underline ml-1">
                {t('pages.search.clearAll')}
              </button>
            )}
          </div>

          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-snow-500" aria-live="polite">
              {loading
                ? t('pages.search.loading')
                : `${t('common.resultsCount', { count: total })}${qDebounced ? t('pages.search.forQuery', { q: qDebounced }) : ''}${type ? t('pages.search.inType', { type: typeLabel(type) }) : ''}`}
            </p>
          </div>

          {error ? (
            <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg text-sm mb-4" role="alert">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-16 text-snow-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> {t('pages.search.loading')}
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<SearchIcon className="w-7 h-7" />}
              title={qDebounced || type ? t('pages.search.empty.titleQuery') : t('pages.search.empty.titleStart')}
              description={
                qDebounced || type
                  ? t('pages.search.empty.descQuery')
                  : t('pages.search.empty.descStart')
              }
              action={
                <div className="flex gap-2 justify-center">
                  <Link to="/resources" className="btn-outline btn-sm">{t('pages.search.empty.browseResources')}</Link>
                  <Link to="/knowledge" className="btn-primary btn-sm">{t('pages.search.empty.knowledgeHub')}</Link>
                </div>
              }
            />
          ) : (
            <>
              <ul className="space-y-3">
                {items.map((r) => {
                  const meta = TYPE_META[r.type]
                  const Icon = meta.icon
                  return (
                    <li key={`${r.type}-${r.id}`}>
                      <Card hover className="p-4 sm:p-5">
                        <Link to={r.path} className="flex items-start gap-4 group">
                          <span
                            className="shrink-0 w-11 h-11 rounded-lg flex items-center justify-center"
                            aria-hidden="true"
                          >
                            <span className={`w-11 h-11 rounded-lg flex items-center justify-center bg-[rgb(240_252_255)] text-[rgb(15_76_129)]`}>
                              <Icon className="w-5 h-5" />
                            </span>
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <Badge tone="slate">{typeLabel(r.type)}</Badge>
                              {r.category && <Badge tone="blue">{r.category}</Badge>}
                            </div>
                            <h3 className="font-semibold text-snow-900 group-hover:text-[rgb(15_76_129)] line-clamp-2">
                              {r.title}
                            </h3>
                            {r.excerpt && (
                              <p className="text-sm text-snow-600 mt-1 line-clamp-2">{r.excerpt}</p>
                            )}
                            <p className="text-xs text-snow-500 mt-1.5 truncate">{r.path}</p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-snow-300 shrink-0 mt-1 group-hover:text-[rgb(0_180_216)]" aria-hidden="true" />
                        </Link>
                      </Card>
                    </li>
                  )
                })}
              </ul>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="btn-outline btn-sm disabled:opacity-50">
                    {t('common.previous')}
                  </button>
                  <span className="text-xs text-snow-500">{t('common.pageXofY', { page: page + 1, total: totalPages })}</span>
                  <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="btn-outline btn-sm disabled:opacity-50">
                    {t('common.next')}
                  </button>
                </div>
              )}
            </>
          )}
        </Container>
      </Section>
    </PortalLayout>
  )
}